/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1685404800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue May 30 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "workday-adaptive-planning",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Workday Adaptive Planning",
          "depth": 0,
          "id": "connect-to-workday-adaptive-planning"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing modeled sheets",
          "depth": 1,
          "id": "syncing-modeled-sheets"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Modeled sheets"
              })
            }), _jsx(_components.td, {
              children: "Import data to a selected modeled sheet"
            }), _jsx(_components.td, {
              children: "Insert, Mirror"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://adaptiveplanning.doc.workday.com/r/DG7oXjCPB6kIw6Th6awNUg/~SS8TWR04JTiriCxM4rMBA",
                children: "Modeled sheet docs"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-workday-adaptive-planning",
        children: "Connect to Workday Adaptive Planning"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Workday Adaptive Planning"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Workday Adaptive Planning"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Workday ", _jsx(_components.strong, {
            children: "Username"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Workday ", _jsx(_components.strong, {
            children: "Password"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've entered these fields, click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Give your destination a descriptive name and ", _jsx(_components.strong, {
          children: "Finish"
        }), " the destination connection setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Workday Adaptive Planning destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Workday Adaptive Planning destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Workday Adaptive Planning destination allows you to ", _jsx(_components.strong, {
          children: "Mirror"
        }), " your data to a spreadsheet or insert new rows with ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Mirror"
          }), " mode replaces the entire selected sheet's contents with the rows from your model.\nEnsure that your sheet doesn't contain data that shouldn't be overwritten if you select this mode."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-modeled-sheets",
        children: "Syncing modeled sheets"
      }), "\n", _jsx(_components.p, {
        children: "You can insert rows into or completely replace the selected modeled sheet with data from your model.\nTo begin, select the sheet you want to sync data to and its version."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Insert"
        }), " and ", _jsx(_components.strong, {
          children: "Mirror"
        }), " mode, Workday Adaptive Planning automatically generates an\nidentifier for every new record synced, so there is no need to match an\nexisting record."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync sheet fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to your modeled sheet fields.\nWorkday Adaptive Planning requires the following fields, so you must map them to complete your configuration:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Approval"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Asset Class"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Level"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Priority"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure your model columns data types match the data types of the fields you want to sync to."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Workday Adaptive Planning uses numbers to represent boolean values. Use ", _jsx(_components.code, {
            children: "0"
          }), " for\n", _jsx(_components.code, {
            children: "false"
          }), " and ", _jsx(_components.code, {
            children: "1"
          }), " for ", _jsx(_components.code, {
            children: "true"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "data-containing-pipe-symbols",
        children: "Data containing pipe symbols"
      }), "\n", _jsxs(_components.p, {
        children: ["Workday Adaptive Planning uses the pipe symbol (", _jsx(_components.code, {
          children: "|"
        }), ") to separate the sheet's columns.\nMake sure your data does not contain any pipe symbols since it will cause a mismatch\nin the header and row data."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
